import { TargetPlatformsDisplayName } from "../../models/targetPlatforms";

type FieldConfig = Readonly<{
  key: string;
  label: string;
  subFields?: ReadonlyArray<
    Readonly<{ key: string; label: string; valueMap?: Readonly<Record<string, string>> }>
  >;
  filter?: (item: Record<string, unknown>) => boolean;
}>;

type SectionFieldConfig = Readonly<{
  [sectionId: string]: ReadonlyArray<FieldConfig>;
}>;

const isSelectedItem = (item: Record<string, unknown>) =>
  item.selected === true || item.selected === "Yes";

const anticipatedCapabilitiesDisplayNames: Record<string, string> = {
  targetNative4k: "Target native 4k resolution or higher",
  hdr: "HDR",
  crossGenerationalContent: "Cross generational content",
  cloudAware: "Cloud aware",
};

const xsCapabilitiesDisplayNames: Record<string, string> = {
  rayTracing: "Ray tracing",
  highFrameRate: "High Frame Rate (HFR) above 60 FPS",
  directStorage: "Direct storage",
};

const localPlayerModesDisplayNames: Record<string, string> = {
  singleMultiplayer: "Single player",
  multiplayer: "Multiplayer",
  couchCoOp: "Couch co-op",
};

const onlinePlayerModesDisplayNames: Record<string, string> = {
  onlineSinglePlayer: "Online single player",
  onlineMultiplayer: "Online multiplayer",
  onlineCoOp: "Online co-op",
};

const targetPlatformsField: FieldConfig = {
  key: "targetPlatforms",
  label: "Target Platforms",
  filter: isSelectedItem,
  subFields: [
    { key: "platform", label: "Platform", valueMap: TargetPlatformsDisplayName },
    { key: "releaseDate", label: "Release Date" },
  ],
};

const anticipatedCapabilitiesField: FieldConfig = {
  key: "anticipatedCapabilities",
  label: "Anticipated Capabilities",
  filter: isSelectedItem,
  subFields: [
    { key: "capability", label: "Capability", valueMap: anticipatedCapabilitiesDisplayNames },
  ],
};

const xboxSeriesCapabilitiesField: FieldConfig = {
  key: "xboxSeriesXSVersionCapabilities",
  label: "Xbox Series X|S Capabilities",
  filter: isSelectedItem,
  subFields: [{ key: "capability", label: "Capability", valueMap: xsCapabilitiesDisplayNames }],
};

const localPlayerModesField: FieldConfig = {
  key: "localPlayerModes",
  label: "Local Player Modes",
  filter: isSelectedItem,
  subFields: [
    { key: "playerMode", label: "Mode", valueMap: localPlayerModesDisplayNames },
    { key: "playersPerConsole", label: "Players per Console" },
  ],
};

const onlinePlayerModesField: FieldConfig = {
  key: "onlinePlayerModes",
  label: "Online Player Modes",
  filter: isSelectedItem,
  subFields: [
    { key: "playerMode", label: "Mode", valueMap: onlinePlayerModesDisplayNames },
    { key: "playersPerConsole", label: "Players per Console" },
  ],
};

export const idXboxSectionFields: SectionFieldConfig = {
  details: [
    { key: "gameTitle", label: "Game Title" },
    { key: "codeName", label: "Code Name" },
    { key: "genre", label: "Genre" },
    { key: "gameDevelopers", label: "Game Developers" },
    { key: "hook", label: "The Hook" },
    { key: "gameplayVideosLinks", label: "Gameplay Videos" },
    { key: "gameplayDetails", label: "Gameplay Details & Summary" },
    { key: "gameplayDuration", label: "Gameplay Duration" },
    { key: "levelsDetails", label: "Levels Details" },
    { key: "levelsNumber", label: "Number of Levels" },
    { key: "gameBuildLinks", label: "Game Build Links" },
    { key: "steamLink", label: "Steam Link" },
    { key: "itchIoLink", label: "Itch.io Link" },
    { key: "gameBuildKeys", label: "Game Build Keys" },
    { key: "additionalLinks", label: "Additional Links" },
  ],
  gameExpectations: [
    { key: "xboxPlayAnywhere", label: "XBOX Play Anywhere" },
    targetPlatformsField,
    { key: "releaseNotes", label: "Release Notes" },
    { key: "middleware", label: "Middleware / Engine" },
    { key: "middlewareVersion", label: "Engine Version" },
    { key: "middlewareNotes", label: "Middleware Notes" },
  ],
  pricingAndAvailability: [
    { key: "monetizationStrategy", label: "Monetization Strategy" },
    { key: "suggestedRetailPrice", label: "Suggested Retail Price" },
    { key: "howItWillGenerateMoney", label: "How It Will Generate Revenue" },
    { key: "retentionMechanics", label: "Retention Mechanics" },
    { key: "hasInGameStore", label: "Has In-Game Store" },
    { key: "inGamePricePoints", label: "In-Game Price Points" },
    { key: "whatPlayersCanSpendOn", label: "What Players Can Spend On" },
    { key: "timeUntilAPlayerIsPromptedToPay", label: "Time Until A Player Is Prompted To Pay" },
    { key: "canPlayersTradeOrGiftItems", label: "Can Players Trade Or Gift Items" },
    { key: "howItemsCanBeGifted", label: "How Items Can Be Gifted" },
    { key: "hasSubscriptionFee", label: "Has Subscription Fee" },
    { key: "subscriptionModel", label: "Subscription Model" },
    { key: "supportedLanguages", label: "Supported Languages" },
    { key: "hasOtherLanguages", label: "Has Other Languages" },
    { key: "otherLanguages", label: "Other Languages" },
    { key: "anticipatedRatings", label: "Anticipated Ratings" },
    { key: "sensitiveContent", label: "Sensitive Content" },
    { key: "sensitiveContentDetails", label: "Sensitive Content Details" },
  ],
  technicalCapabilities: [
    anticipatedCapabilitiesField,
    xboxSeriesCapabilitiesField,
    { key: "xboxSeriesXSAdvantage", label: "Xbox Series X|S Advantage" },
  ],
  configurations: [
    localPlayerModesField,
    onlinePlayerModesField,
    { key: "crossPlay", label: "Cross Play" },
  ],
  xboxNetwork: [
    { key: "xboxNetworkFeatures", label: "Xbox Network Features" },
    { key: "addOns", label: "Add-Ons" },
    { key: "additionalContent", label: "Additional Content" },
  ],
  ugc: [
    { key: "hasUserGeneratedShareableContent", label: "User Generated Content" },
    { key: "ugcDetails", label: "UGC Details" },
  ],
  supplementaryData: [
    {
      key: "devStudioExperience",
      label: "Titles Previously Shipped by Studio",
      subFields: [
        { key: "platform", label: "Platform" },
        { key: "gameTitle", label: "Game Title" },
        { key: "yearShipped", label: "Year Shipped" },
      ],
    },
    {
      key: "teamMembersPreviousTitles",
      label: "Titles Previously Shipped by Team Members",
      subFields: [
        { key: "memberName", label: "Member Name" },
        { key: "gameTitle", label: "Game Title" },
        { key: "platform", label: "Platform" },
        { key: "yearShipped", label: "Year Shipped" },
      ],
    },
    {
      key: "competitorTitles",
      label: "Competitor Titles",
      subFields: [
        { key: "platform", label: "Platform" },
        { key: "gameTitle", label: "Game Title" },
      ],
    },
  ],
};

export const aaaSectionFields: SectionFieldConfig = {
  details: [
    { key: "gameTitle", label: "Game Title" },
    { key: "codeName", label: "Code Name" },
    { key: "genre", label: "Genre" },
    { key: "targetReleaseDate", label: "Target Release Date" },
    { key: "gameDevelopers", label: "Game Developers" },
    { key: "hook", label: "The Hook" },
    { key: "gameplayVideosLinks", label: "Gameplay Videos" },
    { key: "gameplayDetails", label: "Gameplay Details & Summary" },
    { key: "gameplayDuration", label: "Gameplay Duration" },
    { key: "levelsDetails", label: "Levels Details" },
    { key: "levelsNumber", label: "Number of Levels" },
    { key: "gameBuildLinks", label: "Game Build Links" },
    { key: "steamLink", label: "Steam Link" },
    { key: "itchIoLink", label: "Itch.io Link" },
    { key: "gameBuildKeys", label: "Game Build Keys" },
    { key: "additionalLinks", label: "Additional Links" },
  ],
  distributionInformation: [
    { key: "shippingStrategy", label: "Shipping Strategy" },
    { key: "howItWillGenerateMoney", label: "How It Will Generate Revenue" },
    { key: "retentionMechanics", label: "Retention Mechanics" },
    { key: "hasInGameStore", label: "Has In-Game Store" },
    { key: "inGamePricePoints", label: "In-Game Price Points" },
    { key: "whatPlayersCanSpendOn", label: "What Players Can Spend On" },
    { key: "timeUntilAPlayerIsPromptedToPay", label: "Time Until A Player Is Prompted To Pay" },
    { key: "canPlayersTradeOrGiftItems", label: "Can Players Trade Or Gift Items" },
    { key: "howItemsCanBeGifted", label: "How Items Can Be Gifted" },
    { key: "hasSubscriptionFee", label: "Has Subscription Fee" },
    { key: "subscriptionModel", label: "Subscription Model" },
    { key: "isResponsibleForCertification", label: "Responsible For Certification" },
    { key: "certResponsiblePartner", label: "Partner Responsible For Certification" },
    { key: "isPrevIndependentlyPublished", label: "Previously Independently Published" },
    { key: "prevReleases", label: "Previous Releases" },
    { key: "isAssumingResponsibilitesForPrevPublishedGame", label: "Assuming Publishing Rights" },
    { key: "primaryPublisherName", label: "Primary Publisher Name" },
    { key: "publishingTerritories", label: "Publishing Territories" },
  ],
  gameExpectations: [
    { key: "xboxPlayAnywhere", label: "XBOX Play Anywhere" },
    targetPlatformsField,
    { key: "releaseDatesByTerritory", label: "Release Dates by Territory" },
    { key: "demosTrialsBetas", label: "Demos, Trials & Betas" },
    { key: "releaseNotes", label: "Release Notes" },
    { key: "middleware", label: "Middleware / Engine" },
    { key: "middlewareVersion", label: "Engine Version" },
    { key: "middlewareNotes", label: "Middleware Notes" },
  ],
  availability: [
    { key: "supportedLanguages", label: "Supported Languages" },
    { key: "hasOtherLanguages", label: "Has Other Languages" },
    { key: "otherLanguages", label: "Other Languages" },
    { key: "anticipatedRatings", label: "Anticipated Ratings" },
    { key: "sensitiveContent", label: "Sensitive Content" },
    { key: "sensitiveContentDetails", label: "Sensitive Content Details" },
  ],
  technicalCapabilities: [
    anticipatedCapabilitiesField,
    xboxSeriesCapabilitiesField,
    { key: "xboxSeriesXSAdvantage", label: "Xbox Series X|S Advantage" },
    { key: "xboxSeriesXPerfTargets", label: "Xbox Series X Performance Targets" },
    { key: "xboxSeriesSPerfTargets", label: "Xbox Series S Performance Targets" },
    { key: "isInternetConnectionRequired", label: "Internet Connection Required" },
    { key: "internetConnectionDetails", label: "Internet Connection Details" },
    { key: "spatial3dSound", label: "3D Spatial Sound" },
  ],
  configurations: [
    localPlayerModesField,
    onlinePlayerModesField,
    { key: "hasServerBasedGamePlay", label: "Server-Based Gameplay" },
    { key: "playersPerServer", label: "Players per Server" },
    { key: "publisherHostedServices", label: "Publisher Hosted Services" },
    { key: "hasExtraPeripherals", label: "Supports Extra Peripherals" },
    { key: "extraPeripheralsDetails", label: "Extra Peripherals Details" },
    { key: "requiresExtraPeripherals", label: "Requires Extra Peripherals" },
    { key: "requiredExtraPeripheralsDetails", label: "Required Extra Peripherals Details" },
  ],
  xboxNetwork: [
    { key: "xboxNetworkFeatures", label: "Xbox Network Features" },
    { key: "addOns", label: "Add-Ons" },
    { key: "additionalContent", label: "Additional Content" },
    { key: "xboxLiveSignIn", label: "Xbox Live Sign-In Platforms" },
    { key: "otherXboxLiveSignIn", label: "Other Xbox Live Sign-In Platform" },
    { key: "xboxAchievements", label: "Xbox Achievements Platforms" },
    { key: "otherXboxAchievements", label: "Other Xbox Achievements Platform" },
    { key: "xboxCrossPlay", label: "Xbox Cross-Play Platforms" },
    { key: "otherXboxCrossPlay", label: "Other Xbox Cross-Play Platform" },
    { key: "xboxCrossSaveProgress", label: "Xbox Cross-Save Progress Platforms" },
    { key: "otherXboxCrossSaveProgress", label: "Other Xbox Cross-Save Progress Platform" },
    { key: "windowsCrossSaveProgress", label: "Windows Cross-Save Progress Platforms" },
    { key: "otherWindowsCrossSaveProgress", label: "Other Windows Cross-Save Progress Platform" },
  ],
  ugc: [
    { key: "hasUserGeneratedShareableContent", label: "User Generated Content" },
    { key: "ugcDetails", label: "UGC Details" },
  ],
  supplementaryData: [
    {
      key: "devStudioExperience",
      label: "Titles Previously Shipped by Studio",
      subFields: [
        { key: "platform", label: "Platform" },
        { key: "gameTitle", label: "Game Title" },
        { key: "yearShipped", label: "Year Shipped" },
      ],
    },
    {
      key: "teamMembersPreviousTitles",
      label: "Titles Previously Shipped by Team Members",
      subFields: [
        { key: "memberName", label: "Member Name" },
        { key: "gameTitle", label: "Game Title" },
        { key: "platform", label: "Platform" },
        { key: "yearShipped", label: "Year Shipped" },
      ],
    },
    {
      key: "competitorTitles",
      label: "Competitor Titles",
      subFields: [
        { key: "platform", label: "Platform" },
        { key: "gameTitle", label: "Game Title" },
      ],
    },
  ],
};
